import React, { useRef, useState, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import { usePanelStore } from '../../store/panelStore';
import { useSceneViewStore } from '../../store/sceneViewStore';
import { useSceneStore } from '../../store/sceneStore';
import { useProjectStore } from '../../store/projectStore';
import { DEFAULT_SCENE_VIEW_SETTINGS } from '../../../core/types/scene.types';
import { Panel } from '../panels/Panel';
import { SceneToolbar } from './SceneToolbar';
import { SceneCanvas } from './SceneCanvas';
import { SceneStatusBar } from './SceneStatusBar';
import { TransformTool } from '../../../core/types/scene.types';

// Known fixed heights in the panel layout
const PANEL_HEADER_HEIGHT = 32; // h-8
const TOOLBAR_HEIGHT = 36; // h-9
const STATUSBAR_HEIGHT = 24; // h-6
const BORDER_ADJUSTMENT = 2; // For borders

export const SceneViewPanel: React.FC = () => {
  const { panels } = usePanelStore();
  const { setActiveTool, resetView, setZoom, setPan } = useSceneViewStore();
  const { sceneName, currentScenePath } = useSceneStore();
  const currentProject = useProjectStore((s) => s.currentProject);
  const sceneViewPanel = panels['scene-view'];

  // Get project resolution for status bar (fallback to defaults)
  const projectCanvasWidth = currentProject?.resolution?.width ?? DEFAULT_SCENE_VIEW_SETTINGS.canvasWidth;
  const projectCanvasHeight = currentProject?.resolution?.height ?? DEFAULT_SCENE_VIEW_SETTINGS.canvasHeight;

  // Create dynamic title based on current scene
  const dynamicTitle = useMemo(() => {
    if (currentScenePath) {
      // Extract file name from path and remove extension
      const fileName = currentScenePath.split('/').pop()?.replace(/\.scene$/, '') || sceneName;
      return `Scene View - ${fileName}`;
    }
    // Show "Untitled" for scenes that haven't been saved to a file
    return `Scene View - ${sceneName} (Untitled)`;
  }, [currentScenePath, sceneName]);

  // Create modified panel config with dynamic title
  const panelWithDynamicTitle = useMemo(() => {
    if (!sceneViewPanel) return null;
    return {
      ...sceneViewPanel,
      title: dynamicTitle,
    };
  }, [sceneViewPanel, dynamicTitle]);

  const containerRef = useRef<HTMLDivElement>(null);
  const [canvasSize, setCanvasSize] = useState({ width: 800, height: 600 });

  // Extract bounds values for stable dependencies
  const isFloating = sceneViewPanel?.state === 'floating';
  const boundsWidth = sceneViewPanel?.bounds?.width;
  const boundsHeight = sceneViewPanel?.bounds?.height;

  // Memoize calculated size to avoid infinite loops
  const calculatedSize = useMemo(() => {
    if (isFloating && boundsWidth && boundsHeight) {
      return {
        width: Math.max(100, Math.floor(boundsWidth - BORDER_ADJUSTMENT)),
        height: Math.max(100, Math.floor(
          boundsHeight - PANEL_HEADER_HEIGHT - TOOLBAR_HEIGHT - STATUSBAR_HEIGHT - BORDER_ADJUSTMENT
        )),
      };
    }
    return null;
  }, [isFloating, boundsWidth, boundsHeight]);

  // Memoize update function to avoid recreating it
  const updateSize = useCallback(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const newWidth = Math.max(100, Math.floor(rect.width));
      const newHeight = Math.max(100, Math.floor(rect.height));

      setCanvasSize(prev => {
        if (prev.width !== newWidth || prev.height !== newHeight) {
          return { width: newWidth, height: newHeight };
        }
        return prev;
      });
    }
  }, []);

  // Force size update when panel state or bounds change
  useLayoutEffect(() => {
    // If floating and we have calculated size, use it immediately
    if (calculatedSize) {
      setCanvasSize(prev => {
        if (prev.width !== calculatedSize.width || prev.height !== calculatedSize.height) {
          return calculatedSize;
        }
        return prev;
      });
    }

    // Also measure for verification and fallback
    updateSize();
    const timeoutId = setTimeout(updateSize, 100);
    return () => clearTimeout(timeoutId);
  }, [isFloating, boundsWidth, boundsHeight, updateSize, calculatedSize]);

  // Track container size with ResizeObserver
  useEffect(() => {
    updateSize();

    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(updateSize);
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, [updateSize]);

  // Use calculated size for floating panels, measured size otherwise
  const effectiveSize = calculatedSize || canvasSize;

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't capture if input is focused or user is typing in an editor
      const activeElement = document.activeElement;
      if (
        activeElement?.tagName === 'INPUT' ||
        activeElement?.tagName === 'TEXTAREA' ||
        activeElement?.getAttribute('contenteditable') === 'true' ||
        activeElement?.closest('.monaco-editor') ||
        activeElement?.closest('[data-script-editor]')
      ) {
        return;
      }

      const key = e.key.toLowerCase();

      // Tool shortcuts
      const toolShortcuts: Record<string, TransformTool> = {
        v: 'select',
        w: 'move',
        e: 'rotate',
        r: 'scale',
        q: 'pan',
      };

      if (toolShortcuts[key]) {
        e.preventDefault();
        setActiveTool(toolShortcuts[key]);
        return;
      }

      // View shortcuts
      switch (e.key) {
        case 'Home':
          e.preventDefault();
          resetView();
          break;
        case '+':
        case '=':
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            // Zoom in handled in canvas
          }
          break;
        case '-':
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            // Zoom out handled in canvas
          }
          break;
        case '0':
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            setZoom(1);
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [setActiveTool, resetView, setZoom, setPan]);

  if (!panelWithDynamicTitle?.visible) {
    return null;
  }

  return (
    <Panel panel={panelWithDynamicTitle} className="flex flex-col h-full">
      {/* Toolbar - fixed height */}
      <SceneToolbar />

      {/* Canvas container - takes remaining space */}
      <div
        ref={containerRef}
        className="flex-1 min-h-0 overflow-hidden relative"
        style={{ minHeight: 100 }}
      >
        <SceneCanvas width={effectiveSize.width} height={effectiveSize.height} />
      </div>

      {/* Status Bar - fixed at bottom */}
      <SceneStatusBar canvasWidth={projectCanvasWidth} canvasHeight={projectCanvasHeight} />
    </Panel>
  );
};
